/**
 * 🔑 API KEYS DATA CLIENT
 * 
 * Type-safe API key operations following Convex best practices.
 * Provides centralized, testable mutations and queries for CLI and Web.
 */

import { ConvexHttpClient } from "convex/browser";
import { api } from "@dnplace/convex/_generated/api";
import type { FunctionArgs, FunctionReturnType } from "convex/server";
import { BaseDataClient } from "./baseClient";

/**
 * API Keys-specific data client
 */
export class APIKeysClient extends BaseDataClient {
  /**
   * List user's API keys (without secrets)
   */
  async listUserAPIKeys(): Promise<FunctionReturnType<typeof api.apiKeys.listAPIKeys>> {
    return await this.client.query(api.apiKeys.listAPIKeys, {});
  }
  
  /**
   * Get API key details (without secret)
   */
  async getAPIKey(keyId: string): Promise<FunctionReturnType<typeof api.apiKeys.getAPIKey>> {
    return await this.client.query(api.apiKeys.getAPIKey, { keyId });
  }
  
  /**
   * Create a new API key
   * ⚠️ Secret is only returned once!
   */
  async createAPIKey(data: FunctionArgs<typeof api.apiKeys.createAPIKey>): Promise<FunctionReturnType<typeof api.apiKeys.createAPIKey>> {
    return await this.client.mutation(api.apiKeys.createAPIKey, data);
  }
  
  /**
   * Update an existing API key (metadata, permissions, etc.)
   */
  async updateAPIKey(data: FunctionArgs<typeof api.apiKeys.updateAPIKey>): Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>> {
    return await this.client.mutation(api.apiKeys.updateAPIKey, data);
  }
  
  /**
   * Revoke/disable an API key
   */
  async revokeAPIKey(keyId: string): Promise<FunctionReturnType<typeof api.apiKeys.revokeAPIKey>> {
    return await this.client.mutation(api.apiKeys.revokeAPIKey, { keyId });
  }
  
  /**
   * Rotate API key secret
   * Generates a new secret for an existing key while preserving keyId and permissions
   * ⚠️ Returns the new secret - only shown once!
   */
  async rotateAPIKeySecret(keyId: string, reason?: string): Promise<FunctionReturnType<typeof api.apiKeys.rotateAPIKeySecret>> {
    return await this.client.mutation(api.apiKeys.rotateAPIKeySecret, {
      keyId,
      reason
    });
  }
  
  /**
   * Enable an API key (update isActive to true)
   */
  async enableAPIKey(keyId: string): Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>> {
    return await this.client.mutation(api.apiKeys.updateAPIKey, {
      keyId,
      isActive: true
    });
  }
  
  /**
   * Disable an API key (update isActive to false)
   */
  async disableAPIKey(keyId: string): Promise<FunctionReturnType<typeof api.apiKeys.updateAPIKey>> {
    return await this.client.mutation(api.apiKeys.updateAPIKey, {
      keyId,
      isActive: false
    });
  }
  
  /**
   * Get API key usage statistics
   */
  async getAPIKeyUsageStats(
    keyId?: string,
    fromDate?: number, 
    toDate?: number
  ): Promise<FunctionReturnType<typeof api.apiKeys.getAPIKeyUsageStats>> {
    return await this.client.query(api.apiKeys.getAPIKeyUsageStats, {
      keyId,
      fromDate,
      toDate
    });
  }
}

/**
 * Factory function to create an APIKeysClient
 */
export function createAPIKeysClient(client: ConvexHttpClient): APIKeysClient {
  return new APIKeysClient(client);
}
